import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Calendar, Clock, Video, Phone, Mail, MapPin,
  Loader2, CheckCircle, ChevronLeft, ChevronRight, User, Building2,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CANAUX = {
  visio: { icon: Video, label: 'Visioconférence', desc: 'Google Meet', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  telephone: { icon: Phone, label: 'Téléphone', desc: 'Appel direct', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  email: { icon: Mail, label: 'Email', desc: 'Par email', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  presentiel: { icon: MapPin, label: 'Présentiel', desc: 'Sur place', color: 'bg-amber-100 text-amber-700 border-amber-200' },
};

export default function BookingPage() {
  const { linkId } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1); // 1=date, 2=heure, 3=infos, 4=confirmation
  const [selectedDate, setSelectedDate] = useState('');
  const [creneaux, setCreneaux] = useState([]);
  const [selectedHeure, setSelectedHeure] = useState('');
  const [selectedCanal, setSelectedCanal] = useState('visio');
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [form, setForm] = useState({ nom: '', email: '', telephone: '', entreprise: '', message: '' });

  // Charger les infos du lien
  useEffect(() => {
    fetch(`${API_URL}/api/booking/public/${linkId}`)
      .then(r => r.json())
      .then(data => {
        if (data.success) setPageData(data);
        else toast.error('Lien non trouvé');
      })
      .catch(() => toast.error('Erreur chargement'))
      .finally(() => setLoading(false));
  }, [linkId]);

  // Générer les dates disponibles (30 prochains jours)
  const dates = [];
  const horaires = pageData?.horaires || {};
  const joursFr = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
  for (let i = 1; i <= (pageData?.jours_avance || 30); i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    const jour = joursFr[d.getDay()];
    if (horaires[jour]) {
      dates.push({
        date: d.toISOString().split('T')[0],
        jour,
        jourLabel: jour.charAt(0).toUpperCase() + jour.slice(1),
        dateLabel: d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }),
      });
    }
  }

  const loadCreneaux = async (date) => {
    setSelectedDate(date);
    setSelectedHeure('');
    setLoadingSlots(true);
    try {
      const res = await fetch(`${API_URL}/api/booking/public/${linkId}/dispos?date=${date}`);
      const data = await res.json();
      setCreneaux(data.creneaux || []);
      setStep(2);
    } catch {
      toast.error('Erreur chargement créneaux');
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.nom || !form.email) { toast.error('Nom et email requis'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/booking/public/${linkId}/reserver`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          link_id: linkId,
          date: selectedDate,
          heure: selectedHeure,
          duree_minutes: pageData?.duree_defaut || 30,
          canal: selectedCanal,
          ...form,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setConfirmation(data);
        setStep(4);
      } else {
        toast.error(data.detail || 'Erreur réservation');
      }
    } catch {
      toast.error('Erreur réseau');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-green-100 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
    </div>
  );

  if (!pageData) return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
      <Card className="max-w-md"><CardContent className="pt-6 text-center">
        <p className="text-lg font-semibold">Lien de réservation introuvable</p>
        <p className="text-muted-foreground mt-2">Ce lien est peut-être expiré ou désactivé.</p>
      </CardContent></Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50" data-testid="booking-page">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-2xl mb-4">
            <Calendar className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{pageData.titre}</h1>
          {pageData.description && <p className="text-muted-foreground mt-2">{pageData.description}</p>}
          <div className="flex items-center justify-center gap-2 mt-3">
            <Badge variant="outline" className="gap-1"><Clock className="w-3 h-3" />{pageData.duree_defaut} min</Badge>
            {pageData.canaux?.map(c => {
              const canal = CANAUX[c];
              return canal ? <Badge key={c} className={`gap-1 ${canal.color}`}><canal.icon className="w-3 h-3" />{canal.label}</Badge> : null;
            })}
          </div>
        </div>

        {/* Steps */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {['Date', 'Heure', 'Infos', 'Confirmé'].map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                step > i + 1 ? 'bg-emerald-600 text-white' : step === i + 1 ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-300' : 'bg-gray-100 text-gray-400'
              }`}>{step > i + 1 ? '✓' : i + 1}</div>
              <span className={`text-xs ${step === i + 1 ? 'font-semibold' : 'text-muted-foreground'}`}>{s}</span>
              {i < 3 && <ChevronRight className="w-3 h-3 text-gray-300" />}
            </div>
          ))}
        </div>

        {/* Step 1: Date */}
        {step === 1 && (
          <Card>
            <CardHeader><CardTitle className="text-lg">Choisissez une date</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[400px] overflow-y-auto">
                {dates.map(d => (
                  <button
                    key={d.date}
                    onClick={() => loadCreneaux(d.date)}
                    className={`p-3 rounded-xl border-2 text-center transition-all hover:shadow-md ${
                      selectedDate === d.date ? 'border-emerald-500 bg-emerald-50' : 'border-gray-100 hover:border-emerald-200'
                    }`}
                    data-testid={`date-${d.date}`}
                  >
                    <p className="text-xs text-muted-foreground">{d.jourLabel}</p>
                    <p className="font-semibold text-sm">{d.dateLabel}</p>
                  </button>
                ))}
              </div>
              {loadingSlots && <div className="flex justify-center mt-4"><Loader2 className="w-5 h-5 animate-spin" /></div>}
            </CardContent>
          </Card>
        )}

        {/* Step 2: Heure + Canal */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Choisissez un créneau</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setStep(1)}><ChevronLeft className="w-4 h-4 mr-1" />Retour</Button>
              </div>
              <p className="text-sm text-muted-foreground">{new Date(selectedDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Canal */}
              <div>
                <Label className="text-sm font-semibold mb-2 block">Canal de communication</Label>
                <div className="grid grid-cols-2 gap-2">
                  {(pageData.canaux || ['visio']).map(c => {
                    const canal = CANAUX[c];
                    if (!canal) return null;
                    return (
                      <button
                        key={c}
                        onClick={() => setSelectedCanal(c)}
                        className={`p-3 rounded-xl border-2 flex items-center gap-3 transition-all ${
                          selectedCanal === c ? `${canal.color} ring-2 ring-offset-1` : 'border-gray-100 hover:border-gray-200'
                        }`}
                        data-testid={`canal-${c}`}
                      >
                        <canal.icon className="w-5 h-5" />
                        <div className="text-left">
                          <p className="font-medium text-sm">{canal.label}</p>
                          <p className="text-[10px] text-muted-foreground">{canal.desc}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Créneaux */}
              <div>
                <Label className="text-sm font-semibold mb-2 block">Créneaux disponibles</Label>
                {creneaux.length === 0 ? (
                  <p className="text-muted-foreground text-center py-6">Aucun créneau disponible ce jour</p>
                ) : (
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {creneaux.map(h => (
                      <button
                        key={h}
                        onClick={() => { setSelectedHeure(h); setStep(3); }}
                        className={`p-2 rounded-lg border-2 text-center text-sm font-medium transition-all ${
                          selectedHeure === h ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-100 hover:border-emerald-200 hover:bg-emerald-50/50'
                        }`}
                        data-testid={`slot-${h}`}
                      >
                        {h}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Infos personnelles */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Vos informations</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setStep(2)}><ChevronLeft className="w-4 h-4 mr-1" />Retour</Button>
              </div>
              <div className="flex gap-2 mt-1">
                <Badge variant="outline">{new Date(selectedDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</Badge>
                <Badge variant="outline">{selectedHeure}</Badge>
                <Badge className={CANAUX[selectedCanal]?.color}>{CANAUX[selectedCanal]?.label}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nom complet *</Label><Input value={form.nom} onChange={e => setForm(p => ({ ...p, nom: e.target.value }))} placeholder="Jean Dupont" className="mt-1" data-testid="booking-nom" /></div>
                <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="jean@ferme.fr" className="mt-1" data-testid="booking-email" /></div>
                <div><Label>Téléphone</Label><Input value={form.telephone} onChange={e => setForm(p => ({ ...p, telephone: e.target.value }))} placeholder="06 12 34 56 78" className="mt-1" /></div>
                <div><Label>Entreprise</Label><Input value={form.entreprise} onChange={e => setForm(p => ({ ...p, entreprise: e.target.value }))} placeholder="EARL Les Vignes" className="mt-1" /></div>
              </div>
              <div><Label>Message (optionnel)</Label><Textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} placeholder="Objet du RDV..." className="mt-1" rows={2} /></div>
              <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="btn-confirm-booking">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Confirmer le rendez-vous
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && confirmation && (
          <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200">
            <CardContent className="pt-8 text-center space-y-4">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-emerald-800">Rendez-vous confirmé !</h2>
              <div className="space-y-2 text-sm">
                <p><strong>Date :</strong> {new Date(confirmation.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
                <p><strong>Heure :</strong> {confirmation.heure}</p>
                <p><strong>Canal :</strong> {CANAUX[confirmation.canal]?.label}</p>
                {confirmation.meet_link && (
                  <a href={confirmation.meet_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    <Video className="w-4 h-4" />Rejoindre la visio
                  </a>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-4">Une invitation a été envoyée par email. À bientôt !</p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-8">
          Propulsé par Bureau Agricole
        </p>
      </div>
    </div>
  );
}
